/**
 * Formula Builder Utilities
 * Provides safe formula evaluation, parsing, and validation for formula fields
 */

/**
 * Safely evaluates a mathematical formula expression
 * @param {string} expression - The formula expression to evaluate (e.g., "field_1 * field_2 / (field_1 + field_2)")
 * @param {Object} fieldValues - Object mapping field IDs to their numeric values
 * @returns {Object} - { success: boolean, value?: number, error?: string }
 */
export const evaluateFormula = (expression, fieldValues) => {
  try {
    // Convert ^ to ** for JavaScript evaluation (exponentiation support)
    const convertedExpression = expression.replace(/\^/g, '**');

    // Sanitize expression - allow numbers, operators, parentheses, field names
    // Temporarily replace ** with a placeholder to avoid regex issues
    let sanitized = convertedExpression.replace(/\*\*/g, '§§'); // Placeholder for **
    sanitized = sanitized.replace(/[^a-zA-Z0-9_+\-*/().\s§]/g, '');
    sanitized = sanitized.replace(/§§/g, '**'); // Restore **

    // Verify sanitization didn't change the expression unexpectedly
    const expectedSanitized = convertedExpression.replace(/\*\*/g, '§§').replace(/[^a-zA-Z0-9_+\-*/().\s§]/g, '').replace(/§§/g, '**');
    if (sanitized !== expectedSanitized) {
      throw new Error('Formula contains invalid characters');
    }

    // Create safe evaluation context
    const safeContext = {};
    Object.keys(fieldValues).forEach(key => {
      const value = parseFloat(fieldValues[key]);
      if (isNaN(value)) {
        throw new Error(`Field "${key}" is not a number`);
      }
      safeContext[key] = value;
    });

    // Create function with restricted scope
    // This is safer than eval() as it limits available variables
    const func = new Function(...Object.keys(safeContext), `return (${sanitized});`);
    const result = func(...Object.values(safeContext));

    // Check for division by zero or invalid results
    if (!isFinite(result)) {
      throw new Error('Division by zero or invalid calculation');
    }

    return { success: true, value: result };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Parses a formula expression to extract referenced field IDs
 * @param {string} expression - The formula expression
 * @returns {Array<string>} - Array of unique field IDs found in the expression
 */
export const parseFormulaFields = (expression) => {
  // Match field IDs (alphanumeric + underscore, not starting with number)
  const fieldPattern = /\b[a-zA-Z_][a-zA-Z0-9_]*\b/g;
  const matches = expression.match(fieldPattern) || [];

  // Filter out JavaScript keywords and operators
  const keywords = ['return', 'function', 'if', 'else', 'while', 'for', 'const', 'let', 'var'];
  const uniqueFields = [...new Set(matches.filter(m => !keywords.includes(m)))];

  return uniqueFields;
};

/**
 * Validates formula syntax
 * @param {string} expression - The formula expression to validate
 * @returns {Object} - { valid: boolean, error?: string }
 */
export const validateFormulaSyntax = (expression) => {
  try {
    // Check for empty expression
    if (!expression || !expression.trim()) {
      return { valid: false, error: 'Formula cannot be empty' };
    }

    // Check balanced parentheses
    let depth = 0;
    for (let char of expression) {
      if (char === '(') depth++;
      if (char === ')') depth--;
      if (depth < 0) {
        return { valid: false, error: 'Unmatched closing parenthesis' };
      }
    }
    if (depth !== 0) {
      return { valid: false, error: 'Unmatched opening parenthesis' };
    }

    // Check for invalid characters - now allow ^ for exponentiation
    if (/[^a-zA-Z0-9_+\-*/().\s^]/g.test(expression)) {
      return { valid: false, error: 'Invalid characters in formula' };
    }

    // Convert ^ to ** for validation
    const normalized = expression.replace(/\^/g, '**');

    // Check for invalid operator sequences
    // Allow ** for exponentiation, but not other consecutive operators
    // This regex checks for invalid sequences while allowing **
    const invalidSequences = /(\+\+)|(--(?!\d))|(\*\*\*)|(\/{2,})|(\+\*(?!\*))|(\*(?!\*)\+)|(\+\/)|(\/-)|(-\/)/g;
    if (invalidSequences.test(normalized)) {
      return { valid: false, error: 'Invalid operator sequence' };
    }

    return { valid: true };
  } catch (error) {
    return { valid: false, error: error.message };
  }
};

/**
 * Detects circular dependencies in formula fields
 * @param {string} fieldId - The ID of the field being checked
 * @param {Array<string>} newDependencies - Array of field IDs that this field depends on
 * @param {Object} allFieldDependencies - Object mapping all field IDs to their dependencies
 * @returns {boolean} - True if circular dependency detected, false otherwise
 */
export const detectCircularDependency = (fieldId, newDependencies, allFieldDependencies) => {
  const visited = new Set();
  const recursionStack = new Set();

  const hasCycle = (currentId) => {
    visited.add(currentId);
    recursionStack.add(currentId);

    // Get dependencies for current field
    const deps = currentId === fieldId ? newDependencies : (allFieldDependencies[currentId] || []);

    for (let dep of deps) {
      if (!visited.has(dep)) {
        if (hasCycle(dep)) return true;
      } else if (recursionStack.has(dep)) {
        // Found a back edge - circular dependency detected
        return true;
      }
    }

    recursionStack.delete(currentId);
    return false;
  };

  return hasCycle(fieldId);
};
